/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';

import { IDefaLandingPageData } from './defa-landing-page.data';
import { IDefaLandingPageProps } from './defa-landing-page.props.autogenerated';

export interface IDefaLandingPageViewProps extends IDefaLandingPageProps<IDefaLandingPageData> {
    LandingPage: IModuleProps;
    heading?: JSX.Element;
    description?: JSX.Element;
    links: JSX.Element;
    backToDEFALink?: JSX.Element;
    backgroundImageStyle?: JSX.Element;
}

/**
 *
 * DefaLandingPage component
 * @extends {React.PureComponent<IDefaLandingPageProps<IDefaLandingPageData>>}
 */
class DefaLandingPage extends React.PureComponent<IDefaLandingPageProps<IDefaLandingPageData>> {

    private readonly baseClassName: string = 'defa-landing-page';

    public render(): JSX.Element | null {
        const viewProps: IDefaLandingPageViewProps = {
            ...this.props,
            LandingPage: {
                className: this.baseClassName,
                moduleProps: this.props
            },
            heading: this.renderHeading(),
            description: this.renderDescription(),
            links: this.renderLinks(),
            backToDEFALink: this.renderBackToDEFALink(),
            backgroundImageStyle: this.props.config.backgroundImage?.src ? this.getBackgroundImageStyle() : undefined
        };
        return this.props.renderView(viewProps);
    }

    private renderHeading(): JSX.Element | undefined {
        const { config } = this.props;

        if (!config.heading || !config.heading.text) {
            return;
        }

        return (
            <Msdyn365.Text
                className={`${this.baseClassName}__heading`}
                tag={config.heading?.tag || 'h3'}
                text={config.heading?.text}
            />
        );
    }

    private renderDescription(): JSX.Element | undefined {
        const { config } = this.props;

        if (!config.description) {
            return;
        }

        return (
            <div className={`${this.baseClassName}__description`} dangerouslySetInnerHTML={{ __html: config.description as string }} />
        );
    }

    private renderLinks(): JSX.Element {
        const { config } = this.props;

        return (
            <div className={`${this.baseClassName}__links`}>
                <ul className={`${this.baseClassName}__links-list`}>
                    {config.links?.map((link, index) => {
                        return (
                            <li key={index} className={`${this.baseClassName}__link`}>
                                <a href={link.linkUrl?.destinationUrl}>
                                    <img
                                        alt={link.countryCode}
                                        src={`https://flagcdn.com/${link.countryCode}.svg`}
                                        width='30'
                                    />
                                    <span>
                                        {link.text}
                                    </span>
                                </a>
                            </li>
                        );
                    })}
                </ul>
            </div>
        );
    }

    private renderBackToDEFALink(): JSX.Element | undefined {
        const { config, resources } = this.props;

        if (!config.backToDEFAUrl || !config.backToDEFAUrl.destinationUrl) {
            return;
        }

        return (
            <a href={config.backToDEFAUrl.destinationUrl} className={`${this.baseClassName}__back-to-defa-link`}>
                {resources.goBackToDEFAText}
            </a>
        );
    }

    private getBackgroundImageStyle(): JSX.Element | undefined {
        return (
            <Msdyn365.HtmlHeadInclude>
                <style>
                    {`
                    #main {
                        background-image: url(${this.props.config.backgroundImage?.src});
                        background-repeat: no-repeat;
                        background-size: cover;
                        background-position: center; 
                    }
                    `}
                </style>
            </Msdyn365.HtmlHeadInclude>
        );
    }
}

export default DefaLandingPage;
